import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HttpTypes } from '@marxbiotech/shared-types/medusa';

import { api } from '@lib/api/client';
import { ordersKeys } from '@lib/query/options/orders';

export interface CancelOrderInput {
  orderId: string;
}

interface CancelOrderResponse {
  order: HttpTypes.StoreOrder;
}

export const cancelOrder = async (
  input: CancelOrderInput
): Promise<HttpTypes.StoreOrder> => {
  const response = await api.post<CancelOrderResponse>(
    `/orders/${input.orderId}/cancel`
  );
  return response.data.order;
};

interface UseCancelOrderOptions {
  onSuccess?: (data: HttpTypes.StoreOrder) => void;
  onError?: (error: Error) => void;
}

export function useCancelOrder(options?: UseCancelOrderOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelOrder,
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error, variables) => {
      Sentry.captureException(error, {
        tags: { mutation: 'cancelOrder' },
        extra: { variables },
      });
      options?.onError?.(error);
    },
    // onSettled 不論成敗都重刷 order caches，避免失敗請求可能已變更伺服器狀態時 UI 殘留舊資料
    // invalidate detail(id) 會透過 prefix 匹配連帶清掉 detailAggregated 與 detailConfirmed
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ordersKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: ordersKeys.detail(variables.orderId),
      });
    },
  });
}
